import { api } from '../../../lib/api';
import type {
  OrderablePurchaseRequest,
  PurchaseOrderDetail,
  PurchaseOrderListItem,
  PurchaseOrderListQuery,
  PurchaseOrderRequest,
} from './purchaseOrderTypes';
import type { PagedResult } from '../../administration/users/userTypes';

function toQueryString(query: PurchaseOrderListQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);

  /*
    Each facet is sent whenever the key is PRESENT, empty string included: an empty value is the
    reader saying "none of these", which matches no row. A truthiness check would drop it and the
    server would read the axis as unmentioned — the three-state facet rule.
  */
  if (query.status !== undefined) params.set('status', query.status);
  if (query.stage !== undefined) params.set('stage', query.stage);
  if (query.supplier !== undefined) params.set('supplier', query.supplier);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

/**
 * "We have committed to buy this" — the register, and the lifecycle around it.
 *
 * **`create` places ONE order.** The wizard routes a pool of request lines to one or more
 * suppliers and calls it once per supplier group, so a split across three suppliers is three
 * orders with three approvals.
 *
 * **Nothing here approves or rejects.** Those are the approval engine's endpoints, behind
 * `approvals.decide`, and they reach this module through its own handler.
 */
export const purchaseOrdersApi = {
  list: (query: PurchaseOrderListQuery, signal?: AbortSignal) =>
    api<PagedResult<PurchaseOrderListItem>>(`/api/purchase-orders${toQueryString(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) =>
    api<PurchaseOrderDetail>(`/api/purchase-orders/${id}`, { signal }),

  /**
   * The purchase requests an order may be composed from, with their orderable lines.
   *
   * **Carries `create`, not `read`** — it discloses every approved request's still-unordered
   * lines with their estimated prices, which is a wider fact than the register of past orders.
   */
  orderableRequests: (signal?: AbortSignal) =>
    api<OrderablePurchaseRequest[]>('/api/purchase-orders/orderable-requests', { signal }),

  create: (body: PurchaseOrderRequest) =>
    api<PurchaseOrderDetail>('/api/purchase-orders', { method: 'POST', body }),

  /** Corrects a DRAFT. Refused on anything past it. */
  update: (id: string, body: PurchaseOrderRequest) =>
    api<PurchaseOrderDetail>(`/api/purchase-orders/${id}`, { method: 'PUT', body }),

  /** Routes a draft for approval. Its own key, separate from update. */
  submit: (id: string) =>
    api<PurchaseOrderDetail>(`/api/purchase-orders/${id}/submit`, { method: 'POST' }),

  /**
   * Records that an approved order has gone out to the supplier.
   *
   * **`purchase-orders.send`, its own key** — this is what opens the order to a goods receipt,
   * so the grant reaches past this module into what Logistics may receive. One way: an order
   * sent in error is cancelled, not un-sent.
   */
  markSent: (id: string) =>
    api<PurchaseOrderDetail>(`/api/purchase-orders/${id}/send`, { method: 'POST' }),

  /** Cancels it, closing any approval and releasing its quantities. NOT the same as deleting. */
  cancel: (id: string, reason: string) =>
    api<PurchaseOrderDetail>(`/api/purchase-orders/${id}/cancel`, {
      method: 'POST',
      body: { reason },
    }),

  remove: (id: string) => api<void>(`/api/purchase-orders/${id}`, { method: 'DELETE' }),

  restore: (id: string) => api<void>(`/api/purchase-orders/${id}/restore`, { method: 'POST' }),
};
